import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { ContentContext, FormValuesContext } from '../../index.contexts';
import { SearchMobileProps } from './searchMobile.types';
import SearchMobileModalLazy from './searchMobileModalLazy';
import { StyledSearchMobile } from './searchMobile.styles';
import { CALENDAR_STATE, DESTINATION_STATE } from '../../index.constants';
import { DATES, DESTINATION } from '../../../constants/StoreConstants';
import CommonUtils from '../../../utils/CommonUtils';
import { DateObjectType } from '../../../utils/DateUtils';
import { isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import {
  DATE_FORMAT_DEFAULT,
  DATE_MOBILE_FORMAT_DEFAULT,
  DATE_MOBILE_FORMAT_DEFAULT_DE,
  DATE_MOBILE_FORMAT_FR,
  DATE_MOBILE_FORMAT_JA,
  IS_LOCALE_ZH_TW,
} from '../../../constants/ApplicationConstants';
import { hardCodedAllLocale } from '../../index.constants';
import { usePageContext } from '../../../../../../context/PageContext/index';
import { renderedData } from '../../SearchFormComponent';
import { StyledSearchContainer } from '../../index.styles';
import clsx from 'clsx';
import { FORM_STATE } from '../../index.constants';
import { getDateText } from './utils';
import { FLEXIBLE_FORMAT_YEAR_MONTH } from '../../../../../../constants/Calendar.constant';

const SearchMobile: FC<SearchMobileProps> = ({
  className,
  isHideDestinationProps = false,
  isPublishUserSelection = false,
  isHideFlexibleDates = false,
  showDatesOnMobile = false,
  isSearchPage = false,
  enableTwoSeparateButtonsForDates = false,
  sessionToken,
  searchContainerRef,
  desktopFormTopPadding,
  isADFPage,
  setSearchLoadForm,
  isBulgariDomain,
}) => {
  const {
    placeholderTextMobile,
    dateFormat,
    flexibleTabText,
    stayDates,
    nights,
    night,
    flexible: flexibleLabelText,
    datesPlaceholderText,
    destination,
    hideDestination,
    editField,
    mobileDateFormatLabel,
    flexibleDateFormatLabel,
  } = useContext(ContentContext);
  const { tabSelected, monthSelected, yearSelected } = useContext(renderedData);
  const [showLazyLoadedComponents, setShowLazyLoadedComponents] = useState<boolean>(false);

  const clickTrackingLoc = isSearchPage ? 'Search Form' : '';

  /* states */
  const [dialogOpenState, setDialogOpenState] = useState(false);
  const [searchState, setSearchState] = useState('');

  /* event handlers */
  // set form form state on dialog open
  const openDialog = (state: string): void => {
    setSearchState(state);
    setDialogOpenState(true);
  };
  const { formValues } = useContext(FormValuesContext);

  const { fromDate, toDate, flexible } = formValues?.[DATES] ?? {};
  const numberOfNights = CommonUtils.getNumberOfNights(fromDate, toDate);
  const { currentLocale, sessionData, findHotelsRemoteSPA } = usePageContext();

  let displayedDateFormat = '';

  if (tabSelected === flexibleTabText) {
    displayedDateFormat = flexibleDateFormatLabel ?? FLEXIBLE_FORMAT_YEAR_MONTH;
  } else if (isSearchPage && currentLocale === hardCodedAllLocale?.en) {
    displayedDateFormat = DATE_MOBILE_FORMAT_DEFAULT;
  } else if (isSearchPage && currentLocale === hardCodedAllLocale?.de) {
    displayedDateFormat = DATE_MOBILE_FORMAT_DEFAULT_DE;
  } else if (mobileDateFormatLabel && mobileDateFormatLabel !== 'null') {
    displayedDateFormat = mobileDateFormatLabel;
  } else {
    displayedDateFormat = (dateFormat as string) ?? DATE_FORMAT_DEFAULT;
  }

  // based on numberOfNights, set label  to 'Dates' or '1 Night' or '2 Nights'
  let labelText = stayDates;
  if (numberOfNights > 0) {
    if (numberOfNights > 9 && isADFPage) {
      labelText = `1 ${numberOfNights > 1 ? nights : night}${flexible && !isADFPage ? ` (${flexibleLabelText})` : ''}`;
    } else {
      labelText = `${numberOfNights} ${numberOfNights > 1 ? nights : night}${
        flexible && !isADFPage ? ` (${flexibleLabelText})` : ''
      }`;
    }
  }

  const displayText = useMemo(
    () =>
      getDateText(
        datesPlaceholderText,
        fromDate,
        toDate,
        isSearchPage,
        showDatesOnMobile,
        tabSelected,
        flexibleTabText,
        currentLocale,
        monthSelected,
        yearSelected,
        displayedDateFormat
      ),
    [
      datesPlaceholderText,
      fromDate,
      toDate,
      isSearchPage,
      showDatesOnMobile,
      tabSelected,
      flexibleTabText,
      currentLocale,
      monthSelected,
      yearSelected,
    ]
  );

  /**
   * Method to check if the unsuccessful user journey alert should be displayed
   * @returns boolean
   */
  function isUnsuccessfulUserJourney(): boolean {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params?.has('unsuccessfulSell') && params?.get('unsuccessfulSell') === 'true';
    } else {
      return false;
    }
  }

  const sessionUnsuccessfulDestination =
    sessionData?.cacheData?.data?.AriesCommon?.prop_name ?? sessionData?.data?.AriesCommon?.prop_name;

  useEffect(() => {
    // On Client side, render the hidden fields
    setSearchLoadForm();
    setShowLazyLoadedComponents(true);
  }, []);

  return (
    <>
      {showLazyLoadedComponents && (
        <StyledSearchContainer
          desktopFormTopPadding={desktopFormTopPadding}
          className={clsx(
            'search__container',
            hideDestination ? 'search__container-hide-destination' : '',
            'search-form-shop-page-container custom-scrollbar m-container-fullbleed ',
            isADFPage ? 'adf-search-form-container-mobile m-container-fullbleed ' : '',
            'standard',
            findHotelsRemoteSPA ? 'serp-mobile-height-for-spa' : ''
          )}
          ref={searchContainerRef}
          id="document_search_form_container_el_ref" // used in another fragment to traverse on document node to get the element ref.
          tabIndex={-1}
        >
          <StyledSearchMobile
            className={
              className +
              ` container m-search-container ${isADFPage ? 'search-form-adf' : ''} ${
                isSearchPage ? 'mobile-search-page-form-container' : ''
              }`
            }
          >
            {!isHideDestinationProps ? (
              <button className="search-primary-destination" onClick={(): void => openDialog(DESTINATION_STATE)}>
                {/* for sticky state, show only icon */}
                <span className="icon-location" />
                {/* for non sticky state, show icon and label */}
                <span className="t-font-xs icon-location">{destination}</span>
                {formValues?.[DESTINATION]?.displayText !== '' || isUnsuccessfulUserJourney() ? (
                  <span className="t-subtitle-m t-label-alt-s destination-input-label">
                    {isUnsuccessfulUserJourney()
                      ? sessionUnsuccessfulDestination
                      : formValues?.[DESTINATION]?.displayText}
                  </span>
                ) : (
                  <span className="t-subtitle-m">{placeholderTextMobile as string}</span>
                )}
              </button>
            ) : null}
            <button
              onClick={(): void => openDialog(CALENDAR_STATE)}
              className={`${isHideDestinationProps ? `no-padding` : ``} ${
                isSearchPage ? 'custom_click_track' : ''
              } overflow-hidden`}
              {...{ custom_click_track_value: `${clickTrackingLoc}| Date Picker  button click |internal` }}
            >
              {/* for sticky state, show only icon */}
              <span className="icon-nav---book" />
              {/* for non sticky state, show icon and label */}
              <span className={clsx('t-font-xs icon-nav---book dates-placeholder', isADFPage ? 'hide-icon' : '')}>
                {`${stayDates}`} {showDatesOnMobile && <span className="search-date-stay">({labelText})</span>}
              </span>
              <span
                className={`t-subtitle-inverse-l search-date-label t-label-alt-s ${
                  showDatesOnMobile ? 'search-date-specific' : ''
                }`}
              >
                {displayText}
              </span>
            </button>
            {isADFPage && (
              <button
                className={clsx('m-button-s', 'm-button-secondary', 'search-form-edit-button', 'custom_click_track')}
                custom_click_track_value={`${clickTrackingLoc}| Edit Search Button |internal`}
                onClick={(): void => openDialog(FORM_STATE)}
              >
                <span className={clsx({ nowrap: isGreaterChinaLocale(currentLocale) }, 'edit-cta-content')}>
                  {editField as string}
                </span>
              </button>
            )}
            {!dialogOpenState ? (
              ''
            ) : (
              <SearchMobileModalLazy
                searchState={searchState}
                setSearchState={setSearchState}
                setDialogOpenState={setDialogOpenState}
                dialogOpenState={dialogOpenState}
                isHideDestinationProps={isHideDestinationProps}
                isPublishUserSelection={isPublishUserSelection}
                isHideFlexibleDates={isHideFlexibleDates}
                isSearchPage={isSearchPage}
                enableTwoSeparateButtonsForDates={enableTwoSeparateButtonsForDates}
                sessionToken={sessionToken}
                isADFPage={isADFPage}
                isBulgariDomain={isBulgariDomain}
              />
            )}
          </StyledSearchMobile>
        </StyledSearchContainer>
      )}
    </>
  );
};

export default SearchMobile;
